import 'Components/vm-video-card/vm-video-card';
import { Contributor } from 'Components/vm-video-card/vm-video-card';
import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { TemplateVersion } from 'Utils/templates';
import { millisecondsToTimestamp } from 'Utils/text';
import Styles from './vm-video-card-related.scss';

/**
 * Thin wrapper around the presentational `<vm-video-card>` for the related-video
 * rail. `vm-video-card` is a generic, framework-agnostic layout that takes
 * structured props (`heading`, a `{name, href}` publisher object, a
 * `{name, href}[]` contributors array, a pre-formatted `duration-label`, etc.).
 *
 * Most props map straight through under `vm-video-card`'s own names (`href`,
 * `heading`, `thumbnail-src`, `views-count`, `created-at`, `publisher`,
 * `contributors`). The one transformation this component owns is the duration:
 *  - `duration` (raw ms) → `duration-label` (formatted "MM:SS")
 *
 * Related cards link the whole card to the video (via `href`) but carry no
 * secondary links: callers pass `publisher`/`contributors` as plain
 * `{name}` objects (no `href`), so they render as plain text.
 *
 * Related cards also carry the `clickAndDisable` double-click guard the Django
 * card templates apply via `onclick="clickAndDisable(this)"`: the first click
 * navigates and installs the global's blocking handler, repeat clicks are
 * swallowed so an impatient double-click is not counted as two views. The
 * anchor lives inside `vm-video-card`'s shadow DOM and the click is composed,
 * so the guard is installed on the host, which the click bubbles through.
 *
 * @attr href          - Resolved video URL (e.g. from the `related_video_url` tag).
 * @attr heading       - Video title (shares `vm-video-card`'s `heading` prop,
 *                        named to avoid shadowing the global HTML `title` attr).
 * @attr thumbnail-src - Thumbnail image URL; empty shows the card placeholder.
 * @attr duration      - Raw video duration in milliseconds; formatted to "MM:SS".
 * @attr views-count   - Raw view count.
 * @attr created-at    - ISO-8601 publish timestamp. Omit to hide the date.
 * @attr from-industry - Industry-sponsored card (e.g. a featured campaign in the
 *                        sidebar). Forwarded to `vm-video-card`, which swaps the
 *                        views/date meta for the "Information from Industry"
 *                        disclosure label. Applies to the latest layout only;
 *                        the `2025.12.1` layouts ignore it.
 * @prop publisher     - `{name, href?}` publisher object, forwarded as-is.
 * @prop contributors  - `{name, href?}[]` contributors, forwarded as-is.
 * @attr template-version - Markup template to render; forwarded to `vm-video-card`.
 */
@customElement('vm-video-card-related')
export class VmVideoCardRelated extends LitElement {
    static override styles = Styles;

    @property({ type: String }) href = '';
    @property({ type: String }) heading = '';
    @property({ type: String, attribute: 'thumbnail-src' }) thumbnailSrc = '';
    @property({ type: Number }) duration = 0;
    @property({ type: Number, attribute: 'views-count' }) viewsCount = 0;
    @property({ type: String, attribute: 'created-at' }) createdAt = '';
    @property({ type: Boolean, attribute: 'from-industry' }) fromIndustry = false;
    @property({ type: Array }) contributors: Array<Contributor> = [];
    @property({ type: Object }) publisher: Contributor | null = null;
    @property({ type: String, attribute: 'template-version' })
    templateVersion: TemplateVersion = TemplateVersion.Latest;

    override connectedCallback() {
        super.connectedCallback();
        // Prevent multiple clicks to avoid ddos and double-counting views
        this.addEventListener('click', () => window.clickAndDisable?.(this), { once: true });
    }

    private get _durationLabel(): string {
        return this.duration > 0 ? millisecondsToTimestamp(this.duration) : '';
    }

    override render() {
        return html`
            <vm-video-card
                href=${this.href}
                heading=${this.heading}
                thumbnail-src=${this.thumbnailSrc}
                duration-label=${this._durationLabel}
                views-count=${this.viewsCount}
                created-at=${this.createdAt}
                ?from-industry=${this.fromIndustry}
                template-version=${this.templateVersion}
                .publisher=${this.publisher}
                .contributors=${this.contributors}
            ></vm-video-card>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-card-related': VmVideoCardRelated;
    }
}
